import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import type { MeDto } from '@bb/shared';
import { api, ApiError, family, type ChildControls, type ConsentRequest, type FamilyChild, type FriendApproval } from '../net/api';
import { Field } from './Field';
import { TwoStepCode } from './TwoStep';
import { ChildVerification } from './Verification';
import { ChildMessaging } from './ChildMessaging';
import { BadgePicker } from './Badges';

function Shell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="family-page">
      <header className="family-head">
        <a className="brand" href="/">Buddy Bay</a>
        <span className="muted">{title}</span>
      </header>
      <main className="family-main">{children}</main>
    </div>
  );
}

function useMe() {
  const [me, setMe] = useState<MeDto | null | undefined>(undefined);
  const load = () => api.me().then(setMe, () => setMe(null));
  useEffect(() => {
    void load();
  }, []);
  return { me, reload: load, setMe };
}

function SignIn({ onDone, hint }: { onDone: (me: MeDto) => void; hint?: string }) {
  const [error, setError] = useState<string>();
  const [ticket, setTicket] = useState<string>();
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    try {
      const r = await api.login({ login: String(f.get('login')), password: String(f.get('password')) });
      if ('twoStep' in r) setTicket(r.ticket);
      else onDone(r);
    } catch (err) {
      setError((err as Error).message);
    }
  }
  if (ticket) return <TwoStepCode ticket={ticket} onDone={onDone} onBack={() => setTicket(undefined)} />;
  return (
    <form onSubmit={submit}>
      {hint && <p className="muted small-print">{hint}</p>}
      <Field id="p-login" name="login" label="Username or email" autoComplete="username" required />
      <Field id="p-password" name="password" label="Password" type="password" autoComplete="current-password" required />
      {error && <p className="form-error" role="alert">{error}</p>}
      <button className="btn wide">Sign in</button>
    </form>
  );
}

function CreateParent({ email, token, onDone }: { email: string; token: string; onDone: () => void }) {
  const [error, setError] = useState<{ text: string; fields?: Record<string, string> }>();
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    try {
      const r = await api.register({
        username: String(f.get('username')),
        email: String(f.get('email')),
        birthDate: String(f.get('birthDate')),
        password: String(f.get('password')),
        consentToken: token,
      });
      if ('pendingParent' in r) return setError({ text: 'Parent accounts must belong to an adult aged 18 or older.' });
      if ('verifyEmail' in r) return setError({ text: `We sent a confirmation link to ${r.email}. Click it, then sign in here to approve.` });
      onDone();
    } catch (err) {
      setError({ text: (err as Error).message, fields: err instanceof ApiError ? err.fields : undefined });
    }
  }
  return (
    <form onSubmit={submit}>
      <Field id="c-username" name="username" label="Choose a username for yourself" autoComplete="username" required maxLength={16} error={error?.fields?.username} />
      <Field id="c-email" name="email" label="Your email" type="email" defaultValue={email} required error={error?.fields?.email} />
      <Field id="c-birth" name="birthDate" label="Your date of birth (you must be 18+)" type="date" required error={error?.fields?.birthDate} />
      <Field id="c-password" name="password" label="Password (10+ characters)" type="password" autoComplete="new-password" required minLength={10} error={error?.fields?.password} />
      {error && <p className="form-error" role="alert">{error.text}</p>}
      <button className="btn wide">Create parent account</button>
      <p className="muted small-print">
        By creating an account you agree to the <a href="/policies/terms">Terms of Service</a> and have read the <a href="/policies/privacy">Privacy Policy</a> and{' '}
        <a href="/policies/children">Children's Privacy Notice</a>.
      </p>
    </form>
  );
}

function WhatWeCollect({ req }: { req: ConsentRequest }) {
  return (
    <div className="notice-box">
      <h3>What Buddy Bay collects about {req.childUsername}</h3>
      <ul>
        <li>Their username, password (stored scrambled) and date of birth{req.quickChatOnly ? '' : ', and their own email address'}.</li>
        <li>Their character, items, coins, friends and game progress.</li>
        <li>
          Every chat message they send, kept for 365 days so moderators can keep the community safe. {req.quickChatOnly ? 'At their age they can only use preset quick-chat phrases.' : 'At 14–16 they can type in public chat unless you turn it off.'}
        </li>
        <li>IP address, device type and play times, used for security, safety and your time limits.</li>
      </ul>
      <p className="small-print">We don't show ads, sell data, or share it with others except where the law requires. You can see their chat, download their data or delete the account at any time from parent controls. See the <a href="/policies/children">Children's Privacy Notice</a>.</p>
    </div>
  );
}

/** Landing page for the parent-consent email. */
export function ParentConsentPage({ token }: { token: string }) {
  const [req, setReq] = useState<ConsentRequest>();
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<string>();
  const [tab, setTab] = useState<'signin' | 'create'>('create');
  const [declining, setDeclining] = useState(false);
  const { me, reload, setMe } = useMe();
  useEffect(() => {
    family.consent(token).then(setReq, (e: Error) => setError(e.message));
  }, [token]);

  async function approve() {
    try {
      const r = await family.approve(token);
      setDone(`You approved ${r.childUsername}. They can sign in and play now.`);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  async function decline() {
    try {
      const r = await family.decline(token);
      setDone(r.deleted ? "Thanks. The account wasn't approved and everything in it has been deleted." : 'Thanks. Nothing was changed.');
    } catch (e) {
      setError((e as Error).message);
    }
  }
  const signOut = async () => {
    await api.logout().catch(() => {});
    setMe(null);
  };

  return (
    <Shell title="Parent approval">
      <section className="panel narrow">
        {done ? (
          <>
            <p className="note-ok">{done}</p>
            <a className="btn wide" href="/family">Open parent controls</a>
          </>
        ) : !req ? (
          error ? <p className="form-error">{error}</p> : <p className="muted">Loading…</p>
        ) : (
          <>
            <h2>Approve {req.childUsername}'s account?</h2>
            <p>
              Someone signed up for Buddy Bay as <strong>{req.childUsername}</strong> (age {req.ageBracket}) and gave <strong>{req.parentEmail}</strong> as their parent's email. They can't play until a parent approves.
            </p>
            <WhatWeCollect req={req} />
            {error && <p className="form-error" role="alert">{error}</p>}
            {me === undefined ? (
              <p className="muted">Checking your sign-in…</p>
            ) : me && me.canBeParent ? (
              <>
                <p>You're signed in as <strong>{me.user.username}</strong>. Approving makes this your parent account for {req.childUsername}.</p>
                <button className="btn wide" onClick={() => void approve()}>Approve and set up controls</button>
                <button className="link-btn" onClick={() => void signOut()}>Not you? Sign out</button>
              </>
            ) : me ? (
              <>
                <p className="form-error">You're signed in as {me.user.username}, which can't be a parent account. Parent accounts must belong to an adult aged 18 or older.</p>
                <button className="btn wide" onClick={() => void signOut()}>Sign out</button>
              </>
            ) : (
              <>
                <h3>First, sign in or create your own parent account</h3>
                <div className="tabs" role="tablist">
                  <button role="tab" aria-selected={tab === 'create'} className={tab === 'create' ? 'tab on' : 'tab'} onClick={() => setTab('create')}>Create a parent account</button>
                  <button role="tab" aria-selected={tab === 'signin'} className={tab === 'signin' ? 'tab on' : 'tab'} onClick={() => setTab('signin')}>I have an account</button>
                </div>
                {tab === 'create' ? <CreateParent email={req.parentEmail} token={token} onDone={() => void reload()} /> : <SignIn onDone={(m) => setMe(m)} />}
              </>
            )}
            <hr />
            {declining ? (
              <div className="row-wrap">
                <span>Don't approve? The account and everything in it will be deleted.</span>
                <button className="btn danger" onClick={() => void decline()}>Don't approve</button>
                <button className="btn ghost" onClick={() => setDeclining(false)}>Cancel</button>
              </div>
            ) : (
              <button className="btn ghost wide" onClick={() => setDeclining(true)}>I don't approve this account</button>
            )}
          </>
        )}
      </section>
    </Shell>
  );
}

const TIMEZONES = [
  'America/St_Johns',
  'America/Halifax',
  'America/Toronto',
  'America/New_York',
  'America/Chicago',
  'America/Winnipeg',
  'America/Regina',
  'America/Denver',
  'America/Edmonton',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Vancouver',
  'America/Anchorage',
  'Pacific/Honolulu',
];
const GAMES: [string, string][] = [
  ['fishing', 'Pier Fishing'],
  ['farm', 'Farm'],
  ['mine', 'Deep Mine'],
  ['gifts', 'Gifts to and from friends'],
  ['derby', 'Driftwood Derby'],
  ['arcade:bomb_squad', 'Bubble Squad (called Bomb Squad from age 15)'],
  ['arcade', 'All arcade games'],
];
const LIMITS = [15, 30, 45, 60, 90, 120, 180, 240];
const toTime = (m: number | null) => (m === null ? '' : `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
const fromTime = (v: string) => {
  const [h, mm] = v.split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(mm) ? h! * 60 + mm! : null;
};

/** Parent controls: one card per linked child, plus friend requests waiting for approval. */
export function FamilyPage() {
  const { me, setMe } = useMe();
  const [data, setData] = useState<{ children: FamilyChild[]; approvals: FriendApproval[] }>();
  const [error, setError] = useState<string>();
  const load = () => family.overview().then(setData, (e: Error) => setError(e.message));
  useEffect(() => {
    if (me) void load();
  }, [me]);

  if (me === undefined) return <Shell title="Parent controls"><p className="muted">Loading…</p></Shell>;
  if (!me) {
    return (
      <Shell title="Parent controls">
        <section className="panel narrow">
          <h2>Parent controls</h2>
          <SignIn onDone={setMe} hint="Sign in with your parent account." />
        </section>
      </Shell>
    );
  }
  const decide = async (id: number, approve: boolean) => {
    try {
      await family.decide(id, approve);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <Shell title={`Parent controls · ${me.user.username}`}>
      {error && <p className="form-error" role="alert">{error}</p>}
      {data && data.approvals.length > 0 && (
        <section className="panel">
          <h2>Friend requests waiting for you</h2>
          {data.approvals.map((a) => (
            <div key={a.id} className="row-wrap approval">
              <span>
                {a.direction === 'outgoing' ? <><strong>{a.childName}</strong> wants to add <strong>{a.otherName}</strong> as a friend.</> : <><strong>{a.otherName}</strong> wants to be friends with <strong>{a.childName}</strong>.</>}
              </span>
              <span className="spacer" />
              <button className="btn small" onClick={() => void decide(a.id, true)}>Approve</button>
              <button className="btn small ghost" onClick={() => void decide(a.id, false)}>Decline</button>
            </div>
          ))}
        </section>
      )}
      {data && data.children.length === 0 && (
        <section className="panel narrow">
          <h2>No linked children yet</h2>
          <p>When your child signs up for Buddy Bay with your email, you'll get an email to approve their account. After that, their settings appear here.</p>
        </section>
      )}
      {data?.children.map((c) => (c.agedOut ? (
        <section key={c.id} className="panel">
          <h2>{c.displayName ?? c.username}</h2>
          <p className="muted">{c.username} is 17 or older now, so parent controls no longer apply.</p>
        </section>
      ) : (
        <ChildCard key={c.id} child={c} onChanged={() => void load()} />
      )))}
    </Shell>
  );
}

function ChildCard({ child, onChanged }: { child: Extract<FamilyChild, { agedOut: false }>; onChanged: () => void }) {
  const [c, setC] = useState<ChildControls>(child.controls);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string }>();
  const [tab, setTab] = useState<'controls' | 'badges' | 'messages' | 'chat' | 'account'>('controls');
  const set = (patch: Partial<ChildControls>) => setC((x) => ({ ...x, ...patch }));
  async function save() {
    try {
      await family.saveControls(child.id, c);
      setMsg({ kind: 'ok', text: 'Saved. Changes apply right away.' });
      onChanged();
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    }
  }
  const hours = c.allowedStart !== null;
  const tz = TIMEZONES.includes(c.timezone) ? TIMEZONES : [c.timezone, ...TIMEZONES];
  return (
    <section className="panel child-card">
      <div className="row-wrap">
        <h2>{child.displayName ?? child.username}</h2>
        <span className="tag">Age {child.ageBracket}</span>
        <span className="spacer" />
        <span className="muted">
          Played today: {child.playedTodayMinutes} min{c.dailyLimitMinutes ? ` of ${c.dailyLimitMinutes}` : ''}
          {child.lastLoginAt && ` · last signed in ${new Date(child.lastLoginAt).toLocaleString()}`}
        </span>
      </div>
      <p className="muted small-print">Username {child.username}{child.otherParents.length > 0 && ` · also supervised by ${child.otherParents.join(', ')}`}</p>
      {child.lockedNow && <p className="note-warn">Locked right now: {child.lockedNow}</p>}
      <div className="tabs" role="tablist">
        {(child.quickChatOnly ? (['controls', 'badges', 'messages', 'chat', 'account'] as const) : (['controls', 'messages', 'chat', 'account'] as const)).map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} className={tab === t ? 'tab on' : 'tab'} onClick={() => setTab(t)}>
            {t === 'controls' ? 'Controls' : t === 'badges' ? 'Badges' : t === 'messages' ? 'Messages' : t === 'chat' ? 'Chat history' : 'Account'}
          </button>
        ))}
      </div>

      {tab === 'controls' && (
        <div className="controls-grid">
          <fieldset>
            <legend>Chat</legend>
            {child.quickChatOnly ? (
              <p>Quick chat only. Players aged 8–13 can only use preset phrases.</p>
            ) : (
              <>
                <label className="check"><input type="radio" name={`chat-${child.id}`} checked={c.chatMode === 'default'} onChange={() => set({ chatMode: 'default' })} /> Public chat allowed (filtered)</label>
                <label className="check"><input type="radio" name={`chat-${child.id}`} checked={c.chatMode === 'quick_only'} onChange={() => set({ chatMode: 'quick_only' })} /> Quick chat only</label>
              </>
            )}
          </fieldset>
          <fieldset>
            <legend>Friends</legend>
            <label className="check"><input type="checkbox" checked={c.friendApproval} onChange={(e) => set({ friendApproval: e.currentTarget.checked })} /> I approve every friend request (sent or received) before it goes through</label>
          </fieldset>
          <fieldset>
            <legend>Daily time limit</legend>
            <select aria-label="Daily time limit" value={c.dailyLimitMinutes ?? ''} onChange={(e) => set({ dailyLimitMinutes: e.currentTarget.value ? Number(e.currentTarget.value) : null })}>
              <option value="">No limit</option>
              {LIMITS.map((m) => <option key={m} value={m}>{m < 60 ? `${m} minutes` : `${m / 60} hour${m > 60 ? 's' : ''}`}</option>)}
            </select>
            <p className="muted small-print">When time runs out they're signed out until tomorrow.</p>
          </fieldset>
          <fieldset>
            <legend>Allowed hours</legend>
            <label className="check"><input type="checkbox" checked={hours} onChange={(e) => set(e.currentTarget.checked ? { allowedStart: 15 * 60, allowedEnd: 20 * 60 } : { allowedStart: null, allowedEnd: null })} /> Only allow play between</label>
            {hours && (
              <div className="row-wrap">
                <input type="time" aria-label="From" value={toTime(c.allowedStart)} onChange={(e) => set({ allowedStart: fromTime(e.currentTarget.value) })} />
                <span>and</span>
                <input type="time" aria-label="Until" value={toTime(c.allowedEnd)} onChange={(e) => set({ allowedEnd: fromTime(e.currentTarget.value) })} />
              </div>
            )}
            <label>
              Your timezone
              <select value={c.timezone} onChange={(e) => set({ timezone: e.currentTarget.value })}>
                {tz.map((z) => <option key={z} value={z}>{z.replace('America/', '').replace('Pacific/', '').replace(/_/g, ' ')}</option>)}
              </select>
            </label>
          </fieldset>
          <fieldset>
            <legend>Games</legend>
            {GAMES.map(([key, name]) => (
              <label key={key} className="check">
                <input
                  type="checkbox"
                  checked={!c.disabledGames.includes(key)}
                  onChange={(e) => set({ disabledGames: e.currentTarget.checked ? c.disabledGames.filter((g) => g !== key) : [...c.disabledGames, key] })}
                />{' '}
                {name}
              </label>
            ))}
          </fieldset>
          <div className="row-wrap">
            <button className="btn" onClick={() => void save()}>Save controls</button>
            {msg && <span className={msg.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{msg.text}</span>}
          </div>
        </div>
      )}
      {tab === 'badges' && <BadgePicker childId={child.id} />}
      {tab === 'messages' && <ChildMessaging childId={child.id} username={child.username} />}
      {tab === 'chat' && <ChildChat childId={child.id} />}
      {tab === 'account' && <ChildAccount child={child} onChanged={onChanged} />}
    </section>
  );
}

function ChildChat({ childId }: { childId: number }) {
  const [rows, setRows] = useState<Awaited<ReturnType<typeof family.chat>>['messages']>([]);
  const [more, setMore] = useState(true);
  const [error, setError] = useState<string>();
  const load = (before?: number) =>
    family.chat(childId, before).then(
      (r) => {
        setRows((x) => (before ? [...x, ...r.messages] : r.messages));
        setMore(r.messages.length === 100);
      },
      (e: Error) => setError(e.message),
    );
  useEffect(() => {
    void load();
  }, [childId]);
  return (
    <div>
      <p className="muted small-print">Every message sent in the last 365 days, newest first. "Not shown" means our filter stopped it.</p>
      {error && <p className="form-error">{error}</p>}
      {rows.length === 0 && <p className="muted">No messages yet.</p>}
      <ul className="chat-history">
        {rows.map((m) => (
          <li key={m.id}>
            <span className="muted small-print">{new Date(m.createdAt).toLocaleString()} · {m.channel.replace('area:', '')} · {m.kind === 'quick' ? 'quick chat' : 'typed'}</span>
            <span>{m.content}</span>
            {!m.wasShown && <span className="tag">not shown</span>}
          </li>
        ))}
      </ul>
      {more && rows.length > 0 && <button className="btn ghost" onClick={() => void load(rows.at(-1)!.id)}>Load older</button>}
    </div>
  );
}

function ChildAccount({ child, onChanged }: { child: Extract<FamilyChild, { agedOut: false }>; onChanged: () => void }) {
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string }>();
  const [closing, setClosing] = useState(false);
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      setMsg({ kind: 'ok', text: ok });
      return true;
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
      return false;
    }
  };
  async function download() {
    try {
      const data = await family.exportData(child.id);
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `buddybay-${child.username}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message });
    }
  }
  return (
    <div className="stack">
      {msg && <p className={msg.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{msg.text}</p>}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const form = e.currentTarget;
          void run(() => family.resetPassword(child.id, String(new FormData(form).get('password'))), `Password changed. ${child.username} was signed out everywhere.`).then((ok) => ok && form.reset());
        }}
      >
        <h3>Set a new password</h3>
        <Field id={`pw-${child.id}`} name="password" label="New password for your child (10+ characters)" type="password" autoComplete="new-password" required minLength={10} />
        <button className="btn ghost">Change password</button>
      </form>
      <ChildVerification childId={child.id} username={child.username} />
      {child.email && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const form = e.currentTarget;
            const f = new FormData(form);
            void run(
              () => family.changeEmail(child.id, String(f.get('newEmail')), String(f.get('password')), String(f.get('code') ?? '') || undefined),
              `We sent a confirm link to the new address. ${child.username}'s old address gets a note too.`,
            ).then((ok) => ok && form.reset());
          }}
        >
          <h3>Change {child.username}'s email</h3>
          <p className="muted small-print">Now {child.email}. Only you can change it: once every 90 days, and not in the account's first 60 days.</p>
          <Field id={`ce-${child.id}`} name="newEmail" label="New email" type="email" required />
          <Field id={`cp-${child.id}`} name="password" label="Your own password" type="password" autoComplete="current-password" required />
          <Field id={`cc-${child.id}`} name="code" label="Your two-step code (if you use two-step)" autoComplete="one-time-code" />
          <button className="btn ghost">Send confirm link</button>
        </form>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const form = e.currentTarget;
          void run(() => family.invite(child.id, String(new FormData(form).get('email'))), 'Invitation sent.').then((ok) => ok && form.reset());
        }}
      >
        <h3>Add another parent or guardian</h3>
        <Field id={`inv-${child.id}`} name="email" label="Their email" type="email" required />
        <button className="btn ghost">Send invitation</button>
      </form>
      <div>
        <h3>Your child's data</h3>
        <button className="btn ghost" onClick={() => void download()}>Download everything we hold</button>
      </div>
      <div>
        <h3>Close account</h3>
        {closing ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void run(() => family.close(child.id, String(new FormData(e.currentTarget).get('password'))), 'The account was closed and its data deleted.').then((ok) => ok && onChanged());
            }}
          >
            <p>This deletes {child.username}'s character, items, friends and progress. Moderation records are kept without their name. This can't be undone.</p>
            <Field id={`close-${child.id}`} name="password" label="Your password, to confirm" type="password" autoComplete="current-password" required />
            <div className="row-wrap">
              <button className="btn danger">Close {child.username}'s account</button>
              <button type="button" className="btn ghost" onClick={() => setClosing(false)}>Cancel</button>
            </div>
          </form>
        ) : (
          <button className="btn ghost" onClick={() => setClosing(true)}>Close this account…</button>
        )}
      </div>
    </div>
  );
}
